'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const STRENGTHS = [
  { label: 'Precision Machining', value: 98, icon: 'CogIcon', color: '#00D4FF', glow: 'rgba(0,212,255,0.4)', category: 'MACHINING' },
  { label: 'Additive Manufacturing', value: 85, icon: 'CubeIcon', color: '#FF6B35', glow: 'rgba(255,107,53,0.4)', category: 'ADDITIVE' },
  { label: 'Sheet Metal Fab', value: 95, icon: 'RectangleStackIcon', color: '#00D4FF', glow: 'rgba(0,212,255,0.4)', category: 'FABRICATION' },
  { label: 'Advanced Composites', value: 80, icon: 'Square3Stack3DIcon', color: '#1E7B3C', glow: 'rgba(30,123,60,0.4)', category: 'COMPOSITES' },
  { label: 'Testing & Validation', value: 99, icon: 'BeakerIcon', color: '#1E7B3C', glow: 'rgba(30,123,60,0.4)', category: 'QUALITY' },
  { label: 'Surface Treatment', value: 92, icon: 'SparklesIcon', color: '#00D4FF', glow: 'rgba(0,212,255,0.4)', category: 'FINISHING' },
];

const STATS = [
  { value: '500+', label: 'Projects Delivered', icon: 'RocketLaunchIcon', color: '#00D4FF' },
  { value: '99.8%', label: 'Quality Rate', icon: 'ShieldCheckIcon', color: '#1E7B3C' },
  { value: '±0.0001"', label: 'Peak Tolerance', icon: 'MagnifyingGlassIcon', color: '#FF6B35' },
  { value: 'AS9100', label: 'Certified', icon: 'DocumentCheckIcon', color: '#1E7B3C' },
];

const CapabilityStrengthBar = () => {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #1A2332 0%, #141C28 100%)',
        border: '1px solid rgba(0,212,255,0.15)',
        boxShadow: '0 0 40px rgba(0,0,0,0.4)',
      }}>

      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between relative overflow-hidden"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,212,255,0.03)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ background: 'linear-gradient(90deg, rgba(0,212,255,0.2) 0%, transparent 50%)' }} />
        <div className="relative flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-accent animate-pulse-glow" />
            <div className="w-1.5 h-1.5 rounded-full bg-accent/50 animate-pulse-glow" style={{ animationDelay: '0.3s' }} />
            <div className="w-1 h-1 rounded-full bg-accent/30 animate-pulse-glow" style={{ animationDelay: '0.6s' }} />
          </div>
          <div>
            <span className="font-cta text-[11px] text-accent tracking-[0.2em]">WINGS BHARATH — MANUFACTURING STRENGTH INDEX</span>
            <p className="font-body text-[10px] text-technical-gray/60 mt-0.5">Real-time capability assessment across all production lines</p>
          </div>
        </div>
        <div className="relative flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{ background: 'rgba(30,123,60,0.1)', border: '1px solid rgba(30,123,60,0.3)' }}>
          <div className="w-1.5 h-1.5 rounded-full bg-aerospace-green animate-pulse" />
          <span className="font-cta text-[10px] text-aerospace-green tracking-wider">ALL SYSTEMS OPERATIONAL</span>
        </div>
      </div>

      <div className="p-6">
        {/* Strength bars grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
          {STRENGTHS.map((s, i) => (
            <div key={s.label} className="group relative p-4 rounded-xl transition-all duration-300 hover:scale-[1.02]"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}>
              {/* Hover glow */}
              <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `radial-gradient(circle at 50% 50%, ${s.glow.replace('0.4', '0.05')} 0%, transparent 70%)` }} />

              <div className="relative flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}>
                  <Icon name={s.icon as any} size={15} style={{ color: s.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-body text-xs font-semibold text-foreground truncate">{s.label}</span>
                    <span className="font-accent text-sm ml-2 flex-shrink-0" style={{ color: s.color }}>{s.value}%</span>
                  </div>
                  <span className="font-cta text-[9px] tracking-wider" style={{ color: `${s.color}80` }}>{s.category}</span>
                </div>
              </div>

              {/* Bar */}
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="h-full rounded-full transition-all duration-1000"
                  style={{
                    width: animated ? `${s.value}%` : '0%',
                    background: `linear-gradient(90deg, ${s.color}, ${s.color}99)`,
                    boxShadow: `0 0 10px ${s.glow}`,
                    transitionDelay: `${i * 0.1}s`,
                  }} />
              </div>

              {/* Tick marks */}
              <div className="flex justify-between mt-1">
                {[0, 25, 50, 75, 100].map((tick) => (
                  <span key={tick} className="font-cta text-[8px] text-technical-gray/30">{tick}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-5"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {STATS.map((stat) => (
            <div key={stat.label} className="relative p-4 rounded-xl overflow-hidden group transition-all duration-300 hover:scale-[1.02]"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="absolute top-0 left-0 right-0 h-px"
                style={{ background: `linear-gradient(90deg, transparent, ${stat.color}60, transparent)` }} />
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${stat.color}12`, border: `1px solid ${stat.color}25` }}>
                  <Icon name={stat.icon as any} size={16} style={{ color: stat.color }} />
                </div>
                <div>
                  <p className="font-accent text-base leading-none text-foreground">{stat.value}</p>
                  <p className="font-cta text-[9px] text-technical-gray/60 tracking-wider mt-1">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CapabilityStrengthBar;
